import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Flex, InputNumber, Progress, Table, Tag, Typography } from 'antd';
import { CONFIDENCE_PERCENTILE_SLICES } from '../numberLearning/config';
import type { LearningConfidenceResult, PercentileConfidenceSlice } from '../numberLearning/types';

const { Text, Title } = Typography;

interface GrauConfiancaAprendizagemProps {
    confidence: LearningConfidenceResult | null;
    numSorteados: number;
}

const getDefaultMinAcertos = (confidence: LearningConfidenceResult) =>
    Math.max(1, Math.ceil(confidence.expectedRandomHits));

const getDefaultPercentualBom = () => 30;

const isCellBom = (
    hits: number,
    count: number,
    cellPercent: number,
    minAcertos: number,
    percentualBomReferencia: number
) => count > 0 && hits >= minAcertos && cellPercent >= percentualBomReferencia;

const getDistributionCellStyle = (
    hits: number,
    count: number,
    cellPercent: number,
    minAcertos: number,
    percentualBomReferencia: number
) => {
    if (count === 0) {
        return { background: '#fafafa' };
    }

    if (isCellBom(hits, count, cellPercent, minAcertos, percentualBomReferencia)) {
        return { background: '#d9f7be' };
    }

    if (hits >= minAcertos || cellPercent >= percentualBomReferencia) {
        return { background: '#fffbe6' };
    }

    return { background: '#fff1f0' };
};

const getSliceValue = (
    slices: PercentileConfidenceSlice[],
    percentile: number,
    field: keyof PercentileConfidenceSlice
) => slices.find((slice) => slice.percentile === percentile)?.[field];

const renderPercentileCell = (
    value: string | number,
    percentile: number,
    confidence: LearningConfidenceResult,
    isBest = false
) => (
    <Flex vertical gap={2} align="center">
        <Text strong={isBest} style={isBest ? { color: '#1677ff' } : undefined}>
            {value}
        </Text>
        {isBest && <Tag color="blue" style={{ margin: 0 }}>Melhor</Tag>}
        {confidence.minPercentileGood?.percentile === percentile
            && confidence.bestPercentile.percentile !== percentile && (
            <Tag color="green" style={{ margin: 0 }}>Menor bom</Tag>
        )}
    </Flex>
);

const GrauConfiancaAprendizagem = ({ confidence, numSorteados }: GrauConfiancaAprendizagemProps) => {
    const [minAcertos, setMinAcertos] = useState<number>(1);
    const [percentualBomReferencia, setPercentualBomReferencia] = useState<number>(30);

    useEffect(() => {
        if (confidence) {
            setMinAcertos(getDefaultMinAcertos(confidence));
            setPercentualBomReferencia(getDefaultPercentualBom());
        }
    }, [confidence]);

    const celulasBom = useMemo(() => {
        if (!confidence) {
            return [];
        }

        const items: Array<{ percentile: number; hits: number; percent: number; count: number }> = [];

        for (let hits = 0; hits <= numSorteados; hits++) {
            CONFIDENCE_PERCENTILE_SLICES.forEach((percentile) => {
                const count = confidence.details.filter(
                    (detail) => (detail.hitsByPercentile[percentile] ?? 0) === hits
                ).length;
                const percent = confidence.drawsEvaluated > 0
                    ? (count / confidence.drawsEvaluated) * 100
                    : 0;

                if (isCellBom(hits, count, percent, minAcertos, percentualBomReferencia)) {
                    items.push({ percentile, hits, percent, count });
                }
            });
        }

        return items;
    }, [confidence, numSorteados, minAcertos, percentualBomReferencia]);

    const colunasComCelulaBom = useMemo(
        () => Array.from(new Set(celulasBom.map((item) => item.percentile))).sort((a, b) => a - b),
        [celulasBom]
    );
    const percentileColumns = useMemo(() => {
        if (!confidence) {
            return [];
        }

        return CONFIDENCE_PERCENTILE_SLICES.map((percentile) => ({
            title: `Top ${percentile}%`,
            dataIndex: `p${percentile}`,
            key: `p${percentile}`,
            align: 'center' as const,
            width: 96,
            onCell: () => ({
                style: {
                    background:
                        confidence.bestPercentile.percentile === percentile
                            ? '#e6f4ff'
                            : confidence.minPercentileGood?.percentile === percentile
                                ? '#f6ffed'
                                : undefined,
                },
            }),
        }));
    }, [confidence]);

    const percentileTableRows = useMemo(() => {
        if (!confidence) {
            return [];
        }

        const { percentileSlices } = confidence;
        const row = (key: string, metric: string, field: keyof PercentileConfidenceSlice, format?: (v: number) => string) => {
            const data: Record<string, string | number> = { key, metric };

            CONFIDENCE_PERCENTILE_SLICES.forEach((percentile) => {
                const raw = getSliceValue(percentileSlices, percentile, field);
                const numeric = typeof raw === 'number' ? raw : 0;
                data[`p${percentile}`] = format ? format(numeric) : numeric;
            });

            return data;
        };

        return [
            row('pool', 'Dezenas no pool', 'poolSize'),
            row('avg', 'Média de acertos', 'avgHits', (v) => v.toFixed(2)),
            row('max', 'Máx. acertos', 'maxHits'),
            row('min', 'Mín. acertos', 'minHits'),
            row('acc', 'Acertividade (%)', 'acertividade', (v) => `${v.toFixed(1)}%`),
            row('random', 'Acertos esperados (aleatório)', 'expectedRandomHits', (v) => v.toFixed(2)),
        ];
    }, [confidence]);

    const distributionRows = useMemo(() => {
        if (!confidence) {
            return [];
        }

        const rows = [];
        for (let hits = 0; hits <= numSorteados; hits++) {
            const row: Record<string, string | number> = {
                key: hits,
                hits,
            };

            CONFIDENCE_PERCENTILE_SLICES.forEach((percentile) => {
                const count = confidence.details.filter(
                    (detail) => (detail.hitsByPercentile[percentile] ?? 0) === hits
                ).length;
                const percent = confidence.drawsEvaluated > 0
                    ? (count / confidence.drawsEvaluated) * 100
                    : 0;

                row[`count${percentile}`] = count;
                row[`percent${percentile}`] = percent;
            });

            rows.push(row);
        }

        return rows;
    }, [confidence, numSorteados]);

    const distributionPercentileColumns = useMemo(() => {
        if (!confidence) {
            return [];
        }

        return CONFIDENCE_PERCENTILE_SLICES.map((percentile) => ({
            title: `Top ${percentile}%`,
            key: `p${percentile}`,
            align: 'center' as const,
            width: 100,
            onHeaderCell: () => ({
                style: {
                    background:
                        colunasComCelulaBom.includes(percentile)
                            ? '#d9f7be'
                            : confidence.bestPercentile.percentile === percentile
                                ? '#e6f4ff'
                                : undefined,
                },
            }),
            onCell: (record: Record<string, string | number>) => {
                const hits = Number(record.hits);
                const count = Number(record[`count${percentile}`] ?? 0);
                const cellPercent = Number(record[`percent${percentile}`] ?? 0);

                return {
                    style: getDistributionCellStyle(
                        hits,
                        count,
                        cellPercent,
                        minAcertos,
                        percentualBomReferencia
                    ),
                };
            },
            render: (_: unknown, record: Record<string, string | number>) => {
                const hits = Number(record.hits);
                const count = Number(record[`count${percentile}`] ?? 0);
                const cellPercent = Number(record[`percent${percentile}`] ?? 0);
                const isBom = isCellBom(
                    hits,
                    count,
                    cellPercent,
                    minAcertos,
                    percentualBomReferencia
                );

                return (
                    <Flex vertical gap={2} align="center">
                        <Text strong={isBom} style={isBom ? { color: '#237804' } : undefined}>
                            {count}
                        </Text>
                        <Text
                            type={isBom ? undefined : 'secondary'}
                            strong={isBom}
                            style={{
                                fontSize: 11,
                                color: isBom ? '#237804' : undefined,
                            }}
                        >
                            {cellPercent.toFixed(1)}%
                        </Text>
                        {isBom && (
                            <Tag color="success" style={{ margin: 0, fontSize: 10 }}>
                                Bom
                            </Tag>
                        )}
                    </Flex>
                );
            },
        }));
    }, [confidence, minAcertos, percentualBomReferencia, colunasComCelulaBom]);

    if (!confidence) {
        return (
            <Alert
                type="info"
                showIcon
                message="Grau de confiança indisponível"
                description="Não foi possível calcular a confiança com o histórico disponível."
            />
        );
    }

    const confidenceColor =
        confidence.confidenceScore >= 80 ? 'green'
            : confidence.confidenceScore >= 60 ? 'blue'
                : confidence.confidenceScore >= 40 ? 'gold'
                    : confidence.confidenceScore >= 20 ? 'orange'
                        : 'red';

    return (
        <Flex vertical gap="middle" style={{ width: '100%' }}>
            <Title level={5} style={{ margin: 0 }}>
                Grau de confiança da aprendizagem
            </Title>

            <Alert
                type="info"
                showIcon
                message="Validação retroativa por percentil do ranking"
                description={
                    <>
                        Para cada sorteio validado, verifica quantos números sorteados estavam
                        dentro do top 10%, 20%, 30%… do ranking da aprendizagem naquele momento.
                    </>
                }
            />

            <Flex wrap gap="small">
                <Alert
                    type="success"
                    showIcon
                    style={{ flex: 1, minWidth: 280 }}
                    message={`Melhor percentual: Top ${confidence.bestPercentile.percentile}%`}
                    description={
                        <>
                            Acertividade de {confidence.bestPercentile.acertividade.toFixed(1)}%
                            {' '}(média {confidence.bestPercentile.avgHits.toFixed(2)} acertos,
                            máximo {confidence.bestPercentile.maxHits} de {numSorteados})
                        </>
                    }
                />
                {celulasBom.length > 0 ? (
                    <Alert
                        type="warning"
                        showIcon
                        style={{ flex: 1, minWidth: 280 }}
                        message={`Critério: ≥ ${minAcertos} acerto(s) e frequência da célula ≥ ${percentualBomReferencia}%`}
                        description={
                            <>
                                {celulasBom.length} célula(s) qualificada(s) em{' '}
                                {colunasComCelulaBom.map((p) => `Top ${p}%`).join(', ')}.
                            </>
                        }
                    />
                ) : (
                    <Alert
                        type="warning"
                        showIcon
                        style={{ flex: 1, minWidth: 280 }}
                        message="Nenhuma célula atende aos dois critérios"
                        description={
                            <>
                                Ajuste o mínimo de acertos ou o percentual bom da célula
                                (frequência % exibida em cada célula da distribuição).
                            </>
                        }
                    />
                )}
            </Flex>

            <Table
                style={{ width: '100%' }}
                bordered
                size="small"
                pagination={false}
                rowKey="key"
                scroll={{ x: true }}
                title={() => 'Acertividade por percentual do ranking'}
                columns={[
                    {
                        title: 'Métrica',
                        dataIndex: 'metric',
                        key: 'metric',
                        fixed: 'left',
                        width: 220,
                    },
                    ...percentileColumns.map((column) => ({
                        ...column,
                        render: (value: string | number, record: { key: string }) => {
                            const percentile = Number(String(column.key).replace('p', ''));
                            const isBest = record.key === 'acc'
                                && confidence.bestPercentile.percentile === percentile;

                            return renderPercentileCell(value, percentile, confidence, isBest);
                        },
                    })),
                ]}
                dataSource={percentileTableRows}
            />

            <Table
                style={{ width: '100%' }}
                bordered
                size="small"
                pagination={false}
                rowKey="metric"
                columns={[
                    { title: 'Métrica', dataIndex: 'metric', key: 'metric', width: '45%' },
                    { title: 'Valor', dataIndex: 'value', key: 'value' },
                ]}
                dataSource={[
                    {
                        key: '1',
                        metric: 'Sorteios avaliados',
                        value: confidence.drawsEvaluated,
                    },
                    {
                        key: '2',
                        metric: `Acertos máximos (Top ${confidence.bestPercentile.percentile}%)`,
                        value: `${confidence.maxHits} de ${numSorteados}`,
                    },
                    {
                        key: '3',
                        metric: `Média de acertos (Top ${confidence.bestPercentile.percentile}%)`,
                        value: confidence.avgHits.toFixed(2),
                    },
                    {
                        key: '4',
                        metric: 'Grau de confiança',
                        value: (
                            <Flex vertical gap={6} style={{ maxWidth: 320 }}>
                                <Flex align="center" gap={8}>
                                    <Tag color={confidenceColor}>{confidence.confidenceLabel}</Tag>
                                    <Text strong>{confidence.confidenceScore.toFixed(1)}%</Text>
                                </Flex>
                                <Progress
                                    percent={confidence.confidenceScore}
                                    strokeColor={confidenceColor === 'gold' ? '#faad14' : undefined}
                                    size="small"
                                />
                            </Flex>
                        ),
                    },
                ]}
            />

            <Flex wrap gap="middle" align="flex-end" justify="space-between">
                <Flex wrap gap="middle">
                    <Flex vertical gap={4}>
                        <Text type="secondary">Mínimo de acertos</Text>
                        <InputNumber
                            min={0}
                            max={numSorteados}
                            value={minAcertos}
                            onChange={(value) => setMinAcertos(Number(value) ?? 0)}
                        />
                    </Flex>
                    <Flex vertical gap={4}>
                        <Text type="secondary">Percentual bom (% da célula)</Text>
                        <InputNumber
                            min={0}
                            max={100}
                            value={percentualBomReferencia}
                            onChange={(value) => setPercentualBomReferencia(Number(value) ?? 0)}
                        />
                    </Flex>
                </Flex>
                <Flex wrap gap="small">
                    <Tag color="success">Verde = freq. ≥ % bom e acertos ≥ mín.</Tag>
                    <Tag color="gold">Amarelo = atende só um critério</Tag>
                    <Tag color="red">Vermelho = abaixo dos dois</Tag>
                    <Tag color="blue">Azul = melhor acertividade</Tag>
                </Flex>
            </Flex>

            {celulasBom.length > 0 && (
                <Flex wrap gap={8}>
                    <Text type="secondary">Células bom:</Text>
                    {celulasBom.map((item) => (
                        <Tag key={`${item.percentile}-${item.hits}`} color="success">
                            Top {item.percentile}% · {item.hits} ac. · {item.percent.toFixed(1)}%
                        </Tag>
                    ))}
                </Flex>
            )}

            <Table
                style={{ width: '100%' }}
                bordered
                size="small"
                pagination={false}
                rowKey="hits"
                scroll={{ x: true }}
                title={() => 'Distribuição de acertos por percentual do ranking'}
                columns={[
                    {
                        title: 'Acertos',
                        dataIndex: 'hits',
                        key: 'hits',
                        width: 80,
                        fixed: 'left',
                        onCell: (record: Record<string, string | number>) => ({
                            style: {
                                fontWeight: Number(record.hits) >= minAcertos ? 600 : 400,
                                background: Number(record.hits) >= minAcertos ? '#f6ffed' : undefined,
                            },
                        }),
                        render: (hits: number) => (
                            <Flex vertical gap={2} align="center">
                                <Text>{hits}</Text>
                                {hits >= minAcertos && (
                                    <Tag color="success" style={{ margin: 0, fontSize: 10 }}>
                                        ≥ mín.
                                    </Tag>
                                )}
                            </Flex>
                        ),
                    },
                    ...distributionPercentileColumns,
                ]}
                dataSource={distributionRows}
            />
        </Flex>
    );
};

export default GrauConfiancaAprendizagem;
